import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { type TefComsCustomerAttributeBase } from '@common/customerAttributes/TefComsCustomerAttributeBase';
import { type CustomerAttribute, type CustomerAttributeGroupItem } from '@common/customerAttributes/types';
import { hasContent } from '@common/lit/hasContent';
import { replaceO2SpanWithAirFontLigature } from '@common/utils/dsone/replaceO2SpanWithAirFontLigature';
import type { TefComsCustomerAttribute } from '@src/components/tef-coms-customer-attribute';
import { type TefComsCustomerAttributeInline } from '@src/components/tef-coms-customer-attribute-inline';
import { localDate, localTime } from '@src/components/templates/util';

type HTMLElementWithHide = HTMLElement & { hide: () => void, show: () => void };

export function getCustomerAttributesWithBulletpointTemplateResult(customerAttribute: CustomerAttribute, type: 'standard' | 'extra') {
  return customerAttribute[type]!.items.map((item: CustomerAttributeGroupItem) => html`
    <one-list-item>${unsafeHTML(item.title)}</one-list-item>
  `);
}

export function getCustomerAttributesWithIconTemplateResult(customerAttribute: CustomerAttribute) {
  return customerAttribute.extra!.items.filter((item) => item.display).map((item: CustomerAttributeGroupItem) => html`
    <one-list-item>
      <one-icon slot="icon" name="${item.granted ? 'check' : 'cancel'}" variant="outline" size="xs"></one-icon>
      ${unsafeHTML(item.title)}
    </one-list-item>
  `);
}

export function getCustomerAttributesWithCheckboxTemplateResult(
  tefComsCustomerAttribute: TefComsCustomerAttributeBase,
  customerAttribute: CustomerAttribute,
) {
  return customerAttribute.extra!.items.map((item: CustomerAttributeGroupItem) => html`
    <one-cluster gap="xs" no-wrap>
      <one-checkbox .checked=${item.granted} data-cattr-id="${customerAttribute.id}" data-cattr-item-id="${item.id}" @click=${() => { tefComsCustomerAttribute.save(); }}></one-checkbox>
      <one-text as="span">${unsafeHTML(item.title)}</one-text>
    </one-cluster>
  `);
}

export function getExtraCustomerAttributeItemsTemplateResult(
  tefComsCustomerAttribute: TefComsCustomerAttributeBase,
  customerAttribute: CustomerAttribute,
  itemsDisplayStyle: string,
) {
  if (!customerAttribute.extra) return [];

  const blocks = [html`<one-text strong>${unsafeHTML(customerAttribute.extra.title || 'Zusatzprodukte')}</one-text>`];

  switch (itemsDisplayStyle) {
    case 'bulletpoint':
      blocks.push(html`
        <one-list icon="bullet-ul-list" icon-variant="solid">
          ${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'extra')}
        </one-list>
      `);
      break;
    case 'icon':
      blocks.push(html`<one-list>${getCustomerAttributesWithIconTemplateResult(customerAttribute)}</one-list>`);
      break;
    case 'checkbox':
      blocks.push(html`${getCustomerAttributesWithCheckboxTemplateResult(tefComsCustomerAttribute, customerAttribute)}`);
      break;
    default:
  }

  return blocks;
}

export function getLastChangeTemplateResult(customerAttribute: CustomerAttribute, itemsDisplayStyle: string) {
  if (itemsDisplayStyle !== 'bulletpoint' && customerAttribute.date) {
    return html`
      <one-list-item size="xs" align-items="center">
        <one-icon name="clock" variant="outline" slot="icon" size="xs"></one-icon>
        <one-content>
          <one-text size="xs">
            Letzte Änderung am ${localDate(customerAttribute.date)} um ${localTime(customerAttribute.date)} Uhr
          </one-text>
        </one-content>
      </one-list-item>
    `;
  } return null;
}

export function getButtonsTemplateResult(tefComsCustomerAttribute: TefComsCustomerAttributeBase, mode: string) {
  const hideModal = () => {
    const modal = document.querySelector('tef-coms')!.shadowRoot!.getElementById(`tef-coms-customer-attribute-modal-${tefComsCustomerAttribute.id}`) as HTMLElementWithHide;
    modal.hide();
  };
  if (mode === 'checkbox') {
    return html`
      <one-button size="small" color="default" variant="solid" @click=${() => { tefComsCustomerAttribute.save(); hideModal(); }}>
        Weiter
      </one-button>
      <one-spacer size="fill"></one-spacer>
      <one-button size="small" color="default" variant="solid" @click=${() => { tefComsCustomerAttribute.cancel(); hideModal(); }}>
        Abbrechen
      </one-button>
    `;
  } return html`
    <one-button size="small" color="default" variant="solid" @click=${hideModal}>
      Zurück
    </one-button>
  `;
}

export const tefComsCustomerAttributeModalDialog = function render(
  this: TefComsCustomerAttributeBase,
  customerAttribute: CustomerAttribute,
  itemsDisplayStyle: string,
  template$: HTMLTemplateElement,
) {
  // The "O2" in our service responses must be replaced by "o2" to trigger
  // the "Air" font ligature for the "o2"
  const title = replaceO2SpanWithAirFontLigature(customerAttribute.title || template$.content.textContent || '');

  return html`
  <one-modal id="tef-coms-customer-attribute-modal-${this.id}" closable="" size="md" variant="default">
    <span slot="label">${unsafeHTML(title)}</span>
    <one-stack gap="md">
      ${hasContent(customerAttribute.header) ? unsafeHTML(`<one-text data-testid="cattr-header">${replaceO2SpanWithAirFontLigature(customerAttribute.header)}</one-text>`) : nothing}
      <one-text strong>${unsafeHTML(customerAttribute.standard.title || 'Standardprodukte')}</one-text>
      <one-list icon="bullet-ul-list" icon-variant="solid">
        ${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'standard')}
      </one-list>
      ${getExtraCustomerAttributeItemsTemplateResult(this, customerAttribute, itemsDisplayStyle)}
      ${getLastChangeTemplateResult(customerAttribute, itemsDisplayStyle)}
      ${hasContent(customerAttribute.footer) ? html`<one-text as="p" data-testid="cattr-footer">${unsafeHTML(customerAttribute.footer)}</one-text>` : nothing}
    </one-stack>
    <one-button-group slot="footer">${getButtonsTemplateResult(this, itemsDisplayStyle)}</one-button-group>
  </one-modal>
  `;
};

export const tefComsCustomerAttributeModalTrigger = function render(
  this: TefComsCustomerAttribute,
  template$: HTMLTemplateElement,
) {
  const showModal = () => {
    const modal = document.querySelector('tef-coms')!.shadowRoot!.getElementById(`tef-coms-customer-attribute-modal-${this.id}`) as HTMLElementWithHide;
    modal.show();
  };
  // The "O2" in our service responses must be replaced by "o2" to trigger
  // the "Air" font ligature for the "o2"
  const content = (template$.content.textContent || '').replace('O2', 'o2');

  return html`<one-link as="span" color="prominent" id="tef-coms-customer-attribute-${this.id}" @click=${showModal}>${content}</one-link>`;
};

//
// Displays ONE customer attributes inline instead of in a modal dialog
//
export const tefComsCustomerAttributeInline = function render(
  this: TefComsCustomerAttributeInline,
  customerAttribute: CustomerAttribute,
) {
  const checkboxesHtml = customerAttribute.extra!.items.map((item: CustomerAttributeGroupItem) => html`
    <one-cluster gap="xs" noWrap>
      <one-checkbox .checked=${item.granted} data-cattr-id="${customerAttribute.id}" data-cattr-item-id="${item.id}" ?disabled="${!item.display}"></one-checkbox>
      <one-text as="span">${unsafeHTML(item.title)}</one-text>
    </one-cluster>
  `);

  // The "O2" in our service responses must be replaced by "o2" to trigger
  // the "Air" font ligature for the "o2"
  const title = replaceO2SpanWithAirFontLigature(customerAttribute.title || '')!;

  return html`
    <one-stack gap="md" id="tef-coms-customer-attribute-inline">
      ${hasContent(title) ? unsafeHTML(`<one-heading data-testid="cattr-title" level="h3">${title}</one-heading>`) : nothing}
      ${hasContent(customerAttribute.header) ? unsafeHTML(`<one-text data-testid="cattr-header">${replaceO2SpanWithAirFontLigature(customerAttribute.header)}</one-text>`) : nothing}
      <one-text strong>${unsafeHTML(customerAttribute.standard.title || 'Standardprodukte')}</one-text>
      <one-list icon="bullet-ul-list" icon-variant="solid">
        ${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'standard')}
      </one-list>
      <one-text strong>${unsafeHTML(customerAttribute.extra!.title || 'Zusatzprodukte')}</one-text>
      ${checkboxesHtml}
      ${getLastChangeTemplateResult(customerAttribute, 'checkbox')}
      ${hasContent(customerAttribute.footer) ? html`<one-text as="p" data-testid="cattr-footer">${unsafeHTML(customerAttribute.footer)}</one-text>` : nothing}
      <one-button-group>
        <one-button size="small" color="default" variant="solid" @click=${() => { this.save(); }} ?disabled=${!this.customerAttributeItemsStatusChanged}>
          Speichern
        </one-button>
        <one-spacer size="fill"></one-spacer>
        <one-button size="small" color="default" variant="solid" @click=${() => { this.cancel(); }}>
          Abbrechen
        </one-button>
      </one-button-group>
    </one-stack>
  `;
};
